import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "moment/locale/he";
import { calculateEndTimeOfAppointment } from "../../utils/calculateEndTimeOfAppointment";
import AppointmentDetailsModal from "../../components/appointmentDetailsModal";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { format } from "date-fns";
import { ClockIcon1Svg } from "../../svgs/clockIcon1Svg";
import { UserIcon1Svg } from "../../svgs/userIcon1Svg";
import * as dates from "../../utils/dates";
import CreateAppointmentModal from "../../components/createAppointmentModal";
import APPOINTMENTS from "../../api/services/APPOINTMENTS";
import { toast } from "react-toastify";
import styles from "../../styles/_containers/_appointments/bigCalendar.module.scss";
import { useNavigate } from "react-router-dom";
import { paths } from "../../routes/paths";

const DnDCalendar = withDragAndDrop(Calendar);

moment.locale("he");

const localizer = momentLocalizer(moment, {
  formats: {
    timeGutterFormat: "HH:mm",
    dayHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, "dddd", culture),
    dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, "D", culture)} - ${localizer.format(end, "D", culture)}`,
  },
});

const BigCalendar = ({
  appointments,
  setSuccess,
  employees = [],
  employee = "all",
  setEmployee,
  employeeColors = {},
  onSelectAppt,
  loading = false,
}) => {
  const navigate = useNavigate();
  const clickRef = useRef(null);
  const wrapperRef = useRef(null);

  // whatsapp confirm after reschedule
  const [whatsappPrompt, setWhatsappPrompt] = useState(null);

  useEffect(() => {
    return () => window.clearTimeout(clickRef?.current);
  }, []);

  const [currentView, setCurrentView] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 760px)").matches
      ? Views.DAY
      : Views.WEEK
  );

  // ── Hover time indicator ──
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverY, setHoverY] = useState(0);

  const handleMouseMove = useCallback((e) => {
    if (currentView === Views.MONTH) return;
    const timeContent = wrapperRef.current?.querySelector(".rbc-time-content");
    if (!timeContent) return;
    const rect = timeContent.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    if (mouseY < 0 || mouseY > rect.height) { setHoverTime(null); return; }
    const totalMinutes = ((mouseY + timeContent.scrollTop) / timeContent.scrollHeight) * 1440;
    if (totalMinutes < 0 || totalMinutes >= 1440) { setHoverTime(null); return; }
    const h = Math.floor(totalMinutes / 60);
    const m = Math.round((totalMinutes % 60) / 15) * 15;
    const fh = m >= 60 ? h + 1 : h;
    const fm = m >= 60 ? 0 : m;
    setHoverTime(`${String(fh % 24).padStart(2, "0")}:${String(fm).padStart(2, "0")}`);
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    setHoverY(e.clientY - wrapperRect.top);
  }, [currentView]);

  const handleMouseLeave = useCallback(() => setHoverTime(null), []);

  const [isModalOneOpen, setIsModalOneOpen] = useState(null);
  const [isModalTwoOpen, setIsModalTwoOpen] = useState(null);
  const [selected, setSelected] = useState();

  const calculateTime = (date, slot) => {
    let inputDate = date;
    const [hours, minutes] = slot.split(":").map(Number);
    inputDate.setHours(hours);
    inputDate.setMinutes(minutes);
    return inputDate;
  };

  const events = appointments?.map((i) => ({
    id: i._id,
    title: i.serviceId?.name,
    start: calculateTime(new Date(i.date), i.timeSlot),
    end: calculateTime(new Date(i.date), calculateEndTimeOfAppointment(i.timeSlot, i.serviceId?.time)),
    phone: i.userId?.phone,
    userId: i.userId?._id,
    employeeId: i.employeeId,
    serviceId: i.serviceId?._id,
    customerName: i.userId?.name,
    type: i.serviceId?.type,
    createdAt: i.createdAt,
    serviceColor: i.serviceId?.color,
    confirmationStatus: i.confirmationStatus,
    maximumQueue: i.serviceId?.maximumQueue || 1,
    numberOfAttendants: i.numberOfAttendants || 0,
    _raw: i,
  }));

  // ── Event styling: service color > employee color > default ──
  const eventPropGetter = useCallback((event) => {
    // Service color takes priority, then employee color, then default
    const bgColor = event.serviceColor || employeeColors?.[event.employeeId] || "#DE1468";

    // Cancelled events are muted
    const isCancelled = event.confirmationStatus === "CANCELLED" || event.confirmationStatus === "cancelled";
    const isPast = new Date(event.end) < new Date();

    let opacity = 1;
    if (isCancelled) opacity = 0.4;
    else if (isPast) opacity = 0.7;

    return {
      style: {
        backgroundColor: bgColor,
        borderColor: bgColor,
        opacity,
        borderRadius: "6px",
        border: "none",
        borderRight: `4px solid ${bgColor}`,
        color: "#fff",
        fontSize: "12px",
        fontWeight: 600,
      },
    };
  }, [employeeColors]);

  const handleSelectEvent = (e) => {
    if (onSelectAppt) {
      onSelectAppt(e._raw);
    } else if (e.type === "CLASS") {
      navigate(paths.class(e.id, e.employeeId));
    } else {
      setIsModalOneOpen(e);
    }
  };

  const onSelectSlot = useCallback((slotInfo) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setSelected(slotInfo);
      setIsModalTwoOpen(slotInfo);
    }, 250);
  }, []);

  const components = useMemo(
    () => ({
      toolbar: (props) => (
        <CustomToolbar
          {...props}
          employees={employees}
          employee={employee}
          setEmployee={setEmployee}
          onAddAppointment={() => navigate(paths.createAppointment)}
        />
      ),
      event: CustomEvent,
      day: { event: CustomEvent, header: CustomDayHeader },
      week: { event: CustomEvent, header: CustomDayHeader },
      month: { event: CustomEvent },
    }),
    [employees, employee, setEmployee, navigate]
  );

  const defaultDate = useMemo(() => new Date(), []);

  // DRAG AND DROP
  const onEventDrop = useCallback(async ({ event, start }) => {
    const d = new Date(start);
    const newDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const newTimeSlot = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    try {
      await APPOINTMENTS.reschedule({ id: event.id, date: newDate, timeSlot: newTimeSlot });
      toast.success("התור הועבר בהצלחה");
      setSuccess((v) => !v);
      if (event.phone) {
        setWhatsappPrompt({ phone: event.phone, customerName: event.customerName || "", date: newDate, timeSlot: newTimeSlot });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "שגיאה בהעברת התור");
    }
  }, [setSuccess]);

  return (
    <div
      ref={wrapperRef}
      className={styles.container}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ position: "relative" }}
    >
      {hoverTime && currentView !== Views.MONTH && (
        <div className={styles.hoverLine} style={{ top: hoverY }}>
          <span className={styles.hoverTimeLabel}>{hoverTime}</span>
        </div>
      )}

      <DnDCalendar
        defaultDate={defaultDate}
        defaultView={currentView}
        view={currentView}
        localizer={localizer}
        messages={{
          allDay: "כל היום",
          previous: "<",
          next: ">",
          today: "היום",
          month: "חודש",
          week: "שבוע",
          day: "יום",
          agenda: "סדר היום",
        }}
        formats={{
          timeGutterFormat: (date, culture, localizer) =>
            localizer.format(date, "HH:mm", culture),
          dayFormat: (date, culture, localizer) =>
            localizer.format(date, "dddd D", culture),
        }}
        onView={(view) => setCurrentView(view)}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: "100%", minHeight: 0 }}
        selectable={true}
        onSelectSlot={onSelectSlot}
        onSelectEvent={handleSelectEvent}
        components={components}
        step={15}
        timeslots={1}
        showAllEvents={true}
        selected={selected}
        min={new Date(1970, 1, 1, 5, 0, 0)}
        max={new Date(1970, 1, 1, 23, 0, 0)}
        scrollToTime={new Date(1970, 1, 1, 8, 0, 0)}
        draggableAccessor={() => true}
        onEventDrop={onEventDrop}
        resizable={false}
        eventPropGetter={eventPropGetter}
      />

      {loading && (
        <div className={styles.calendarLoading} role="status" aria-live="polite">
          <span className={styles.loadingSpinner} />
          <span>טוען את היומן...</span>
        </div>
      )}

      {isModalOneOpen && (
        <AppointmentDetailsModal
          data={isModalOneOpen}
          setIsOpen={setIsModalOneOpen}
          setSuccess={setSuccess}
        />
      )}

      {isModalTwoOpen && (
        <CreateAppointmentModal
          data={isModalTwoOpen}
          setIsOpen={setIsModalTwoOpen}
          setSuccess={setSuccess}
          currentView={currentView}
        />
      )}

      {/* WHATSAPP CONFIRM after reschedule */}
      {whatsappPrompt && (
        <div className={styles.waOverlay}>
          <div className={styles.waModal}>
            <p className={styles.waTitle}>האם לשלוח ללקוח עדכון?</p>
            <p className={styles.waBody}>
              שלח ל<strong>{whatsappPrompt.customerName}</strong> הודעת וואטסאפ על התור החדש:<br />
              <span className={styles.waDetails}>
                {whatsappPrompt.date.split("-").reverse().join("/")} בשעה {whatsappPrompt.timeSlot}
              </span>
            </p>
            <div className={styles.waBtns}>
              <button
                className={styles.waSendBtn}
                onClick={() => {
                  const msg = encodeURIComponent(
                    `שלום ${whatsappPrompt.customerName}, התור שלך הוזז ל-${whatsappPrompt.date.split("-").reverse().join("/")} בשעה ${whatsappPrompt.timeSlot}. נתראה! 😊`
                  );
                  const phone = whatsappPrompt.phone.replace(/\D/g, "");
                  const intlPhone = phone.startsWith("0") ? `972${phone.slice(1)}` : phone;
                  window.open(`https://wa.me/${intlPhone}?text=${msg}`, "_blank");
                  setWhatsappPrompt(null);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.553 4.12 1.523 5.853L.057 23.617a.75.75 0 00.918.919l5.771-1.466A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.693-.5-5.24-1.375l-.372-.213-3.876.985.999-3.739-.234-.384A9.956 9.956 0 012 12C2 6.478 6.478 2 12 2s10 4.478 10 10-4.478 10-10 10z" />
                </svg>
                שלח וואטסאפ
              </button>
              <button className={styles.waSkipBtn} onClick={() => setWhatsappPrompt(null)}>
                לא עכשיו
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BigCalendar;

// ── Custom Toolbar — pill view switcher ──
const VIEW_OPTIONS = [
  { key: Views.DAY,   label: "יום"  },
  { key: Views.WEEK,  label: "שבוע" },
  { key: Views.MONTH, label: "חודש" },
];

const CustomToolbar = ({ view, onView, onNavigate, label, employees, employee, setEmployee, onAddAppointment }) => {
  return (
    <div className={styles.customToolbar}>
      {/* Right: nav + date label (RTL = rightmost) */}
      <div className={styles.tbRight}>
        <div className={styles.tbNav}>
          <button type="button" className={`${styles.tbBtn} ${styles.todayBtn}`} onClick={() => onNavigate("TODAY")}>היום</button>
          <button type="button" className={`${styles.tbBtn} ${styles.iconBtn}`} onClick={() => onNavigate("PREV")} aria-label="לתקופה הקודמת">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m12.5 5-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button type="button" className={`${styles.tbBtn} ${styles.iconBtn}`} onClick={() => onNavigate("NEXT")} aria-label="לתקופה הבאה">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m7.5 5 5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <span className={styles.tbLabel}>{label}</span>
      </div>

      {/* Left: employee select + view pills + add button */}
      <div className={styles.tbLeft}>
        {employees && employees.length > 1 && (
          <select
            className={styles.tbEmployeeSelect}
            value={employee}
            onChange={(e) => setEmployee && setEmployee(e.target.value)}
            aria-label="סינון לפי עובד"
          >
            <option value="all">כל העובדים</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        )}

        {/* View pill switcher */}
        <div className={styles.tbPills}>
          {VIEW_OPTIONS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={`${styles.tbPill} ${view === v.key ? styles.tbPillActive : ""}`}
              onClick={() => onView(v.key)}
              aria-pressed={view === v.key}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Add appointment */}
        <button type="button" className={styles.tbAddBtn} onClick={onAddAppointment}>
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          הוספת תור
        </button>
      </div>
    </div>
  );
};

// CUSTOM DAY HEADER
const CustomDayHeader = ({ date }) => {
  const isToday = new Date().toDateString() === new Date(date).toDateString();
  const dayNum = moment(date).format("D");
  const dayName = moment(date).format("dd");

  return (
    <div className={styles.customDayHeader}>
      <span className={styles.dayName}>{dayName}</span>
      <div className={isToday ? styles.todayBadge : styles.dayNumWrap}>
        <span className={isToday ? styles.dayNumToday : styles.dayNum}>{dayNum}</span>
      </div>
    </div>
  );
};

// CUSTOM EVENT
const STATUS_DOTS = {
  CONFIRMED: { color: "#22c55e", label: "אושר" },
  DECLINED: { color: "#ef4444", label: "דחה" },
  PENDING: { color: "#f59e0b", label: "ממתין" },
};

const CustomEvent = ({ event }) => {
  const statusDot = STATUS_DOTS[event.confirmationStatus];
  const isClass = event.type === "CLASS";

  return (
    <div className={styles.customEvent}>
      <div className={styles.eventCustomer}>
        <UserIcon1Svg fill="#fff" />
        <p>{isClass ? event.title : event.customerName}</p>
        {statusDot && (
          <span
            className={styles.statusDot}
            style={{
              backgroundColor: statusDot.color,
            }}
            title={statusDot.label}
          />
        )}
      </div>
      {!isClass && <h4 className={styles.title}>{event.title}</h4>}
      <div className={styles.hours}>
        <ClockIcon1Svg fill="#fff" />
        <p>{format(new Date(event.start), "HH:mm")}</p>
        <span>–</span>
        <p>{format(new Date(event.end), "HH:mm")}</p>
        {isClass && (
          <span className={styles.classBadge}>
            {event.numberOfAttendants}/{event.maximumQueue}
          </span>
        )}
      </div>
    </div>
  );
};
